import { POWER_PERIODS } from '@/constants/supplyRates'
import { normalizeContractCups, isValidContractCups } from '@/services/contractCups'
import { emptyRatesForm, ratesFromSupplies, ratesPayload, validatePowers } from '@/services/estateSupplyRates'

/**
 * Bloques "Suministro 1..3" del alta de inmueble (16/09/2026).
 *
 * El cliente rellena los datos del inmueble y del primer suministro; con
 * "Añadir otro suministro" se abre el bloque 2 y luego el 3, cada uno con los
 * mismos campos: dirección, CP, municipio y provincia; CUPS de luz y de gas;
 * tarifa por energía y potencias P1-P6 (solo con tarifa de luz). En el alta
 * inicial caben tres como máximo; el resto se añade desde la ficha (ADS-0124).
 */
export const MAX_SUPPLY_BLOCKS = 3

export const LOCATION_FIELDS = ['address', 'postal_code', 'municipality', 'province']

/**
 * Bloque nuevo. Al abrir el 2 o el 3 se precarga la ubicación del bloque
 * anterior: casi siempre es el mismo inmueble y así el cliente solo corrige
 * lo que cambie.
 */
export const emptySupplyBlock = (location = {}) => ({
  address: '', postal_code: '', municipality: '', province: '',
  ...Object.fromEntries(LOCATION_FIELDS.filter((field) => location?.[field]).map((field) => [field, location[field]])),
  cups_light: '', cups_gas: '',
  ...emptyRatesForm(),
})

/** Un bloque a partir de la ficha guardada (edición): ubicación y tarifas de sus suministros. */
export const supplyBlockFromEstate = (estate = {}) => {
  const block = emptySupplyBlock()
  for (const field of LOCATION_FIELDS) block[field] = estate?.[field] ?? ''
  Object.assign(block, ratesFromSupplies(estate?.supplies))
  return block
}

export const canAddSupplyBlock = (blocks) => Array.isArray(blocks) && blocks.length < MAX_SUPPLY_BLOCKS

/** Errores del bloque como claves de traducción por campo. Todo es opcional; solo se valida el formato. */
export const validateSupplyBlock = (block) => {
  const errors = {}
  if (block.postal_code && !/^\d{4,5}$/.test(String(block.postal_code).trim())) {
    errors.postal_code = 'property.estateForm.validation.postalCode'
  }
  for (const field of ['cups_light', 'cups_gas']) {
    const cups = normalizeContractCups(block[field])
    if (cups !== '' && !isValidContractCups(cups)) errors[field] = 'property.estateSupplies.validation.cups'
  }
  return { ...errors, ...validatePowers(block) }
}

/**
 * Errores de todos los bloques con la clave que devuelve la API:
 * `supplies.<índice>.<campo>`. Un mismo CUPS repetido en dos bloques de la
 * misma energía se marca en ambos antes de llegar a la API.
 */
export const validateSupplyBlocks = (blocks) => {
  const errors = {}
  blocks.forEach((block, index) => {
    Object.entries(validateSupplyBlock(block)).forEach(([field, key]) => { errors[`supplies.${index}.${field}`] = key })
  })
  for (const field of ['cups_light', 'cups_gas']) {
    const seen = new Map()
    blocks.forEach((block, index) => {
      const cups = normalizeContractCups(block[field])
      if (cups === '') return
      if (seen.has(cups)) {
        for (const at of [seen.get(cups), index]) errors[`supplies.${at}.${field}`] ??= 'property.estateForm.validation.duplicateCups'
      } else {
        seen.set(cups, index)
      }
    })
  }
  return errors
}

const clean = (value) => {
  const text = typeof value === 'string' ? value.trim() : value
  return text === '' || text === undefined ? null : text
}

/** Un bloque tal como viaja en `supplies[]` del alta. */
export const supplyBlockPayload = (block) => ({
  ...Object.fromEntries(LOCATION_FIELDS.map((field) => [field, clean(block[field])])),
  cups_light: normalizeContractCups(block.cups_light) || null,
  cups_gas: normalizeContractCups(block.cups_gas) || null,
  ...ratesPayload(block),
})

/** Un bloque sin nada rellenado no viaja: se abrió y no se usó. */
export const isSupplyBlockEmpty = (block) => {
  const payload = supplyBlockPayload(block)
  return Object.values(payload).every((value) => value === null)
}

export const supplyBlocksPayload = (blocks) => blocks.filter((block) => !isSupplyBlockEmpty(block)).map(supplyBlockPayload)

export { POWER_PERIODS }
